<script lang="ts">
	// The design system's Field (design/components/surfaces/Field.jsx): a
	// label, a box, and one line under it.
	//
	// A validation error is amber, not red, as the design says: red is action,
	// and a field that will not take what was typed is something to know, like
	// a cash count that does not match. The error replaces the note rather
	// than stacking under it, and is role=alert.
	//
	// The design draws the box; the app makes it work. With onInput the box is
	// the <input> itself, with a real <label for>, so the tests can fill it by
	// its test id. Without onInput it shows a value typed somewhere else, such
	// as a phone number from the keypad. mono puts the value in the mono face,
	// left to right in both languages, as numbers and codes are. The note, as
	// in the design, can be markup, so a number in it can keep its direction.

	import type { Snippet } from 'svelte';
	import type { FullAutoFill } from 'svelte/elements';
	import Button from './Button.svelte';
	import { keepCharacters } from './characters';

	interface Props {
		testId: string;
		label?: string;
		value: string;
		/** Makes the box an input. Without it the box shows value. */
		onInput?: (value: string) => void;
		/** Leaving the field, for a caller that tidies what was typed. */
		onBlur?: () => void;
		note?: string | Snippet;
		error?: string;
		mono?: boolean;
		onInk?: boolean;
		inputMode?: 'text' | 'numeric' | 'decimal' | 'tel';
		/**
		 * A date picks a day (YYYY-MM-DD), from `min` to `max`, and a time an
		 * hour and a minute (HH:MM), as a publish at a set time takes them. tel is a phone
		 * number. email and password are an owner's sign-in (UC-25).
		 */
		type?: 'text' | 'date' | 'time' | 'tel' | 'search' | 'email' | 'password';
		/** What the browser may fill in, such as email or current-password. */
		autocomplete?: FullAutoFill;
		/**
		 * A password the owner may show while typing it, as the design draws
		 * Show beside it in the box (D17). The words and the toggle's test id.
		 */
		reveal?: { show: string; hide: string; testId: string };
		min?: string;
		max?: string;
		maxLength?: number;
		/**
		 * The most characters the field takes, counted as the core counts
		 * them (an emoji is one), where maxLength counts UTF-16 units as the
		 * browser does. What is typed past it is cut (keepCharacters).
		 */
		maxCharacters?: number;
		/** Test id for the error line, while there is one. */
		errorTestId?: string;
		/** Test id for the note line, while there is one. */
		noteTestId?: string;
		placeholder?: string;
		/** A passcode: typed digits show as dots and the browser never saves them. */
		secret?: boolean;
		/** Several lines, as the design's Field holding a textarea: a reason, a note. */
		rows?: number;
		/**
		 * The name read aloud when the field has no visible label, as the
		 * design's compact inline fields do (an area's fee, a shop's price).
		 */
		ariaLabel?: string;
		/** The input's name, for a form sent without a script (a search). */
		name?: string;
		/**
		 * Fixed text before what is typed, inside the box, as the design's
		 * page address draws "menu.bosla.shop/" before the slug. Needs onInput.
		 */
		prefix?: string;
		/**
		 * The box's own content instead of the input, as the design's Field
		 * takes children: a select, or an input with a fixed prefix.
		 */
		children?: Snippet;
	}

	let {
		testId,
		label,
		value,
		onInput,
		onBlur,
		note,
		error,
		mono = false,
		onInk = false,
		inputMode = 'text',
		type = 'text',
		min,
		max,
		maxLength,
		maxCharacters,
		errorTestId,
		noteTestId,
		placeholder,
		secret = false,
		rows,
		ariaLabel,
		name,
		prefix,
		children,
		autocomplete,
		reveal,
	}: Props = $props();

	const fieldId = $derived(`field-${testId}`);
	const invalid = $derived(Boolean(error));
	// Whether a revealable password is shown. The field's own state: the
	// screen around it has no reason to know.
	let shown = $state(false);
	// An address or a password is never corrected or capitalised as it is typed.
	const literal = $derived(type === 'email' || type === 'password');

	// What the box holds after an edit, cut to maxCharacters. A word the
	// keyboard is still composing stays whole, because changing the box under
	// it makes the keyboard commit the word again; compositionend cuts it. A
	// cut box shows the kept text, the caret after it where the box has one.
	function typed(event: Event): string {
		const box = event.currentTarget as HTMLInputElement | HTMLTextAreaElement;
		if (maxCharacters === undefined || (event as InputEvent).isComposing) return box.value;
		const caret = box.selectionEnd;
		const kept = keepCharacters(box.value, caret ?? box.value.length, maxCharacters);
		if (kept.text !== box.value) {
			box.value = kept.text;
			if (caret !== null) box.setSelectionRange(kept.caret, kept.caret);
		}
		return kept.text;
	}

	// Only a limited box hears the end of a composition, for its cut.
	const composed = $derived(
		onInput && maxCharacters !== undefined ? (event: Event) => onInput(typed(event)) : undefined,
	);
</script>

<div class="field" class:on-ink={onInk}>
	{#if label}
		{#if onInput}
			<label class="label" for={fieldId}>{label}</label>
		{:else}
			<p class="label">{label}</p>
		{/if}
	{/if}
	{#if children}
		<div
			class="box"
			class:mono
			class:invalid
			data-testid={testId}
			aria-invalid={invalid || undefined}
		>
			{@render children()}
		</div>
	{:else if onInput && prefix}
		<!-- The design's Field with children: the prefix and a bare input
		     share the box, read left to right when mono, so the prefix stays
		     before the value in both languages. -->
		<div class="box affixed" class:mono class:invalid dir={mono ? 'ltr' : undefined}>
			<span class="prefix">{prefix}</span>
			<input
				id={fieldId}
				class="bare"
				data-testid={testId}
				type="text"
				{name}
				spellcheck="false"
				autocapitalize="none"
				aria-label={label ? undefined : ariaLabel}
				{placeholder}
				maxlength={maxLength}
				aria-invalid={invalid || undefined}
				{value}
				oninput={(event) => onInput(typed(event))}
				oncompositionend={composed}
				onblur={onBlur}
			/>
		</div>
	{:else if onInput && rows}
		<textarea
			id={fieldId}
			class="box multiline"
			class:invalid
			data-testid={testId}
			aria-label={label ? undefined : ariaLabel}
			{placeholder}
			{rows}
			maxlength={maxLength}
			aria-invalid={invalid || undefined}
			{value}
			oninput={(event) => onInput(typed(event))}
			oncompositionend={composed}
			onblur={onBlur}></textarea>
	{:else if onInput && type === 'password' && reveal}
		<!-- The design's Field holding the password and a text Show beside it. -->
		<div class="box affixed revealed" class:mono class:invalid dir={mono ? 'ltr' : undefined}>
			<input
				id={fieldId}
				class="bare"
				data-testid={testId}
				type={shown ? 'text' : 'password'}
				{name}
				spellcheck="false"
				autocapitalize="none"
				autocomplete={autocomplete ?? 'new-password'}
				aria-label={label ? undefined : ariaLabel}
				maxlength={maxLength}
				aria-invalid={invalid || undefined}
				{value}
				oninput={(event) => onInput(typed(event))}
				oncompositionend={composed}
				onblur={onBlur}
			/>
			<Button
				testId={reveal.testId}
				tone="text"
				size="sm"
				label={shown ? reveal.hide : reveal.show}
				pressed={shown}
				onPress={() => (shown = !shown)}
			/>
		</div>
	{:else if onInput}
		<input
			id={fieldId}
			class="box"
			class:mono
			class:invalid
			data-testid={testId}
			dir={mono ? 'ltr' : undefined}
			type={secret ? 'password' : type}
			{name}
			{min}
			{max}
			spellcheck={literal ? 'false' : undefined}
			autocapitalize={literal ? 'none' : undefined}
			autocomplete={autocomplete ?? (secret ? 'new-password' : undefined)}
			aria-label={label ? undefined : ariaLabel}
			{placeholder}
			inputmode={inputMode}
			maxlength={maxLength}
			aria-invalid={invalid || undefined}
			{value}
			oninput={(event) => onInput(typed(event))}
			oncompositionend={composed}
			onblur={onBlur}
		/>
	{:else}
		<div
			class="box"
			class:mono
			class:invalid
			data-testid={testId}
			aria-invalid={invalid || undefined}
		>
			{#if mono}
				<bdi dir="ltr">{value}</bdi>
			{:else}
				{value}
			{/if}
		</div>
	{/if}
	{#if invalid}
		<p class="error" role="alert" data-testid={errorTestId}>{error}</p>
	{:else if note}
		<p class="note" data-testid={noteTestId}>
			{#if typeof note === 'function'}{@render note()}{:else}{note}{/if}
		</p>
	{/if}
</div>

<style>
	/* A caller that must know where the box starts sets --field-label-gap and
	   --field-label-line on an ancestor and reads the same two values, so the
	   label's height is never written twice (the catalogue's search row). */
	.label {
		display: block;
		margin: 0 0 var(--field-label-gap, var(--space-2));
		line-height: var(--field-label-line, inherit);
		font-size: 13px;
		color: var(--ink-muted);
	}

	.box {
		display: block;
		box-sizing: border-box;
		inline-size: 100%;
		min-block-size: var(--ctl-min);
		margin: 0;
		padding: 11px 13px;
		background: transparent;
		border: 1px solid var(--border-default);
		border-radius: var(--radius-input);
		font-family: var(--font-sans);
		font-size: 15px;
		font-weight: 600;
		color: var(--ink);
		transition: border-color var(--dur-state) var(--ease-out);
	}

	.box.multiline {
		resize: vertical;
		font-weight: 500;
		line-height: var(--leading-snug);
	}

	.box.mono {
		font-family: var(--font-mono);
		text-align: start;
	}

	.box.invalid {
		border-color: var(--ink-flag);
	}

	.affixed {
		display: flex;
		align-items: baseline;
		min-inline-size: 0;
	}

	.revealed {
		align-items: center;
		gap: var(--space-3);
		padding-block: 0;
	}

	.prefix {
		flex: none;
		color: var(--text-muted);
		font-weight: 500;
	}

	.bare {
		flex: 1;
		min-inline-size: 0;
		margin: 0;
		padding: 0;
		border: 0;
		background: transparent;
		font: inherit;
		color: inherit;
	}

	.error,
	.note {
		margin: 8px 0 0;
		font-size: 13px;
		line-height: 1.7;
	}

	.error {
		font-weight: 600;
		color: var(--ink-flag-deep);
	}

	.note {
		color: var(--ink-body);
	}

	.on-ink .label {
		color: var(--on-ink-muted);
	}

	.on-ink .box {
		border-color: var(--rule-on-ink);
		color: var(--on-ink);
	}

	.on-ink .box.invalid {
		border-color: var(--ink-flag);
	}

	.on-ink .error {
		color: var(--on-ink);
	}

	.on-ink .note {
		color: var(--on-ink-body);
	}
</style>
